import { memo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import dayjs from "dayjs";

import LoadingButton from "@mui/lab/LoadingButton";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import RefreshIcon from "@mui/icons-material/Refresh";

import { Connection, useConnectionStore } from "stores/ConnectionStore";
import { SecretState } from "stores/lib";
import { useOrganizationStore } from "stores/OrganizationStore";

import { captureSentryException } from "utility/capture_sentry_exception";
import { Service } from "utility/connections";
import { FOTS } from "utility/typescript";

// -----------------------------------------------------------------------------

const EnvironmentConnectedService = memo(function EnvironmentConnectedService({
  connection,
  service,
}: EnvironmentConnectedServiceProps): JSX.Element {
  const [organizationStore] = useOrganizationStore();
  const [, connectionsActions] = useConnectionStore();
  const [anchorElUser, setAnchorElUser] = useState(null);
  const navigate = useNavigate();

  //
  const clientSecret = connection.clientSecret;
  const [color, status, StatusIcon] = getConfig(
    clientSecret?.state ?? SecretState.RECEIVED
  );
  const extraDetails = service.onExtraDetails?.(service) || null;
  const extraConnectionDetails =
    service.onExtraConnectionDetails?.(connection) ?? null;

  //
  const openMenu = (event: any) => setAnchorElUser(event.currentTarget);
  const closeMenu = () => setAnchorElUser(null as any);

  //
  const [openEdit, setOpenEdit] = useState(false);
  const [editConnectionName, setEditConnectionName] = useState("");
  const [editConnectionUuid, setEditConnectionUuid] = useState("");
  const [editLoading, setEditLoading] = useState(false);

  const openEditDialog = (connection: any) => {
    setOpenEdit(true);
    setEditConnectionName(connection.name);
    setEditConnectionUuid(connection.uuid);
  };

  const closeEditDialog = () => {
    setOpenEdit(false);
    setEditConnectionName("");
    setEditConnectionUuid("");
  };

  const saveEdit = () => {
    setEditLoading(true);
    connectionsActions.editConnection({
      uuid: editConnectionUuid,
      data: {
        name: editConnectionName,
      },
      onSuccess: onEditSuccess,
      onError: onEditError,
    });
  };

  const onEditSuccess = () => {
    setEditLoading(false);
    setEditConnectionName("");
    setEditConnectionUuid("");
    setOpenEdit(false);

    toast.success(`Updated the connection name`, {
      position: "bottom-center",
    });
  };

  const onEditError = () => {
    setEditLoading(false);
    setEditConnectionName("");
    setEditConnectionUuid("");
    setOpenEdit(false);

    toast.error(`Something went wrong updating the connection name`, {
      position: "bottom-center",
    });
  };

  //
  return (
    <>
      <Card
        elevation={1}
        sx={{
          maxHeight: 300,
          position: "relative",
          display: "flex",
          alignItems: "center",
          padding: 1.5,
        }}
      >
        <Stack
          direction="column"
          alignItems="center"
          mx={{ flexBasis: 1, flexGrow: 0.1 }}
        >
          <service.coverImage
            sx={{
              width: "80%",
              height: "80%",
              maxHeight: 90,
              align: "center",
              padding: 1,
            }}
          />
        </Stack>

        <Stack direction="column" mx={{ flexBasis: 2, flexGrow: 0.9 }}>
          <Stack direction="row" alignItems="center">
            <Typography
              variant="h6"
              fontWeight="bold"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: "1",
                WebkitBoxOrient: "vertical",
              }}
            >
              {connection.name}
            </Typography>
            <IconButton
              aria-label="edit"
              onClick={() => {
                openEditDialog(connection);
              }}
            >
              <EditIcon />
            </IconButton>
          </Stack>
          <Typography variant="body1" fontWeight="bold">
            {service.label}
          </Typography>

          <Box sx={{ display: "flex", flexWrap: "wrap" }}>
            <Typography variant="body1" sx={{ mr: 1.5 }}>
              Connected:{" "}
              {clientSecret != null
                ? dayjs(new Date(clientSecret.created)).format("MM/DD/YYYY")
                : "Unknown"}
            </Typography>
            <Typography
              variant="body1"
              sx={{ display: "flex", alignItems: "center", mr: 1.5 }}
            >
              Status: {status} <Box component="span" sx={{ width: 4 }} />
              <StatusIcon color={color} />
            </Typography>
            {extraDetails}
          </Box>
          {extraConnectionDetails}
          {service.notifications ? (
            <Box>
              <Link
                to={`/environment/destinations/${service.url ?? ""}/${
                  connection.uuid
                }/notifications`}
                style={{ textDecoration: "none" }}
              >
                <Button variant="text">Notification Settings</Button>
              </Link>
            </Box>
          ) : null}
        </Stack>

        <IconButton size="large" color="inherit" onClick={openMenu}>
          <MoreVertIcon />
        </IconButton>
        <Menu
          anchorEl={anchorElUser}
          anchorOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          keepMounted
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          open={Boolean(anchorElUser)}
          onClose={closeMenu}
        >
          {service.configUrl ? (
            <MenuItem
              onClick={() => {
                if (!service.configUrl) return;
                navigate(service.configUrl);
              }}
            >
              Configure
            </MenuItem>
          ) : null}
          <MenuItem
            onClick={() => {
              (async () => {
                try {
                  await connectionsActions.deleteConnection({
                    uuid: connection.uuid,
                    onSuccess: () => {
                      connectionsActions.loadConnections({ reload: true });
                    },
                  });
                  service?.onConnect(organizationStore, navigate);
                } catch (error) {
                  captureSentryException(
                    error,
                    `Failed to reconnect to ${service.label} connection`
                  );
                  toast.error(
                    `Failed to reconnect to ${service.label} connection`,
                    {
                      position: "bottom-center",
                    }
                  );
                }
              })();
            }}
          >
            <RefreshIcon />
            Reconnect
          </MenuItem>

          <MenuItem
            onClick={() => {
              (async () => {
                try {
                  await connectionsActions.deleteConnection({
                    uuid: connection.uuid,
                    onSuccess: () => {
                      toast.success(
                        `Deleted ${service.label} connection with name ${connection.name}`,
                        {
                          position: "bottom-center",
                        }
                      );
                      connectionsActions.loadConnections({ reload: true });
                    },
                    onError: () => {
                      toast.error(
                        `Error occurred when deleting the ${service.label} connection with name ${connection.name}.`,
                        {
                          position: "bottom-center",
                        }
                      );
                      connectionsActions.loadConnections({ reload: true });
                    },
                  });
                } catch (error) {
                  captureSentryException(
                    error,
                    `Failed to delete ${service.label} connection`
                  );
                  toast.error(`Failed to delete ${service.label} connection`, {
                    position: "bottom-center",
                  });
                }
              })();
            }}
          >
            <DeleteIcon />
            Disconnect
          </MenuItem>
        </Menu>
      </Card>

      <Dialog onClose={closeEditDialog} open={openEdit}>
        <DialogTitle>Edit Connection Name</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Name your connection something to help distinguish it from the
            others.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            id="name"
            label="Connection name"
            type="text"
            fullWidth
            variant="standard"
            value={editConnectionName}
            onChange={(e: FOTS) => setEditConnectionName(e?.target?.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEditDialog}>Cancel</Button>
          <LoadingButton loading={editLoading} onClick={saveEdit}>
            Save
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
});

export default EnvironmentConnectedService;

// -----------------------------------------------------------------------------

interface EnvironmentConnectedServiceProps {
  connection: Connection;
  service: Service;
}

// -----------------------------------------------------------------------------

function getConfig(
  state: SecretState
): ["error" | "success" | "warning", string, Function] {
  switch (state) {
    case SecretState.ERROR:
    case SecretState.INCORRECT:
      return ["error", "Error", ErrorOutlineIcon];

    case SecretState.CORRECT:
      return ["success", "Connected", CheckCircleOutlineIcon];

    case SecretState.PENDING_EXTERNAL:
    case SecretState.PENDING_INTERNAL:
    case SecretState.RECEIVED:
      return ["warning", "Pending", AccessTimeIcon];

    default:
      return ["warning", "Unknown", HelpOutlineIcon];
  }
}
